import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import { IonButton } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { CaseloadClientRow } from './CaseloadClientRow';
import { CaseloadSearchState, MAX_CLIENTS_PER_SEARCH } from './CaseloadSearchState';
import { AskBeaCard } from './AskBeaCard';

// Questions worth asking across a book of clients rather than one at a time. Offered as examples
// because the useful shape of question here is not obvious from an empty box.
const EXAMPLE_QUESTIONS = [
    'Which clients have a DNR on file, and is it current?',
    'Who has a document or referral that was never followed up?',
    'Which clients have had a hospital or emergency visit recently?'
];

const useStyles = createUseStyles({
    beaAvatar: {
        width: 32,
        height: 32,
        borderRadius: '50%',
        objectFit: 'cover' as const,
        flexShrink: 0
    },

    // The escalation from filtering to asking. Shown only once there is something to ask, because an
    // empty question is not an offer.
    askRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: 'calc(100% - 8px)',
        margin: '8px 4px 14px',
        padding: '10px 12px',
        border: '1px dashed var(--ion-color-step-200)',
        borderRadius: 9,
        cursor: 'pointer',
        background: 'var(--ion-color-step-50)',
        font: 'inherit',
        color: 'inherit',
        textAlign: 'left' as const
    },

    askText: {
        display: 'block',
        fontSize: 14,
        minWidth: 0
    },

    askLead: {
        display: 'block'
    },

    askCaveat: {
        display: 'block',
        fontSize: 12,
        color: 'var(--ion-color-step-600)',
        marginTop: 1
    },

    actions: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '4px 4px 0',
        flexWrap: 'wrap'
    },

    answer: {
        padding: '4px 16px 6px'
    },

    // Shown for as long as the search runs. Before this the card went blank the moment a question
    // was asked and stayed blank until every client had answered, which reads as nothing happening:
    // the only moving part was a Stop button, and pressing it marked every client unavailable and
    // rendered "No clients matched", so the one control that looked useful produced a wrong answer.
    working: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        margin: '4px 16px 14px',
        padding: '11px 13px',
        borderRadius: 10,
        border: '1px solid var(--ion-color-step-150)',
        background: 'var(--ion-color-step-50)'
    },

    workingCopy: {
        flex: 1,
        minWidth: 0,
        fontSize: 13.5,
        lineHeight: 1.45
    },

    workingCount: {
        marginTop: 2,
        fontSize: 12,
        color: 'var(--ion-color-step-600)',
        fontVariantNumeric: 'tabular-nums'
    },

    '@keyframes caseloadThinking': {
        '0%, 80%, 100%': { opacity: 0.25 },
        '40%': { opacity: 1 }
    },

    thinking: {
        display: 'inline-flex',
        gap: 3,
        marginLeft: 6,
        verticalAlign: 'middle'
    },

    // Three dots rather than a spinner: a spinner reads as a page loading, and this is somebody
    // reading. The stagger is what makes it read as thought rather than as a progress bar.
    dot: {
        width: 4,
        height: 4,
        borderRadius: '50%',
        background: 'var(--ion-color-primary)',
        animation: '$caseloadThinking 1.4s ease-in-out infinite'
    },

    track: {
        height: 3,
        marginTop: 7,
        borderRadius: 2,
        background: 'var(--ion-color-step-150)',
        overflow: 'hidden'
    },

    trackFill: {
        height: '100%',
        borderRadius: 2,
        background: 'var(--ion-color-primary)',
        transition: 'width 220ms ease'
    },

    headline: {
        fontSize: 15,
        lineHeight: 1.45,
        color: 'var(--ion-text-color)',
        marginBottom: 4
    },

    groupLabel: {
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: '.04em',
        textTransform: 'uppercase' as const,
        color: 'var(--ion-color-step-600)',
        margin: '14px 0 2px'
    },

    coverage: {
        padding: '10px 16px 14px',
        fontSize: 12.5,
        lineHeight: 1.5,
        color: 'var(--ion-color-step-650)',
        borderTop: '1px solid var(--ion-color-step-100)',
        marginTop: 8
    }
});

/**
 * The one way into a caseload. The same text narrows the client list as it is typed and, on Enter,
 * becomes a question Bea answers by reading every client's record. Two fields were tried first and
 * read as duplicates of each other: they sat a few hundred pixels apart, looked alike, and gave no
 * way to tell which one a given thought belonged in. Keeping the cheap operation instant and making
 * the expensive one an explicit, labelled step is what separates them now.
 */
/**
 * What Bea is doing while she does it.
 *
 * The card shows nothing per client until every client has been read, which is the right call for
 * the answer and leaves a void while it is being assembled. Without this the only moving part was a
 * Stop button, which read as nothing happening.
 */
const SearchProgress: FC<{ readsLabel: string; readCount: number; total: number }> = ({ readsLabel, readCount, total }) => {
    const classes = useStyles();

    return (
        <div className={classes.working} data-id="caseload-search-working">
            <img src="/assets/images/help/helper-bea.png" alt="" className={classes.beaAvatar} />
            <div className={classes.workingCopy}>
                {/* The label names a client when the question was about one, so it carries PHI and
                    stays out of session replay. */}
                <div className="mp-mask">
                    Bea is reading {readsLabel}
                    <span className={classes.thinking} aria-hidden="true">
                        <span className={classes.dot} />
                        <span className={classes.dot} style={{ animationDelay: '160ms' }} />
                        <span className={classes.dot} style={{ animationDelay: '320ms' }} />
                    </span>
                </div>
                <div className={classes.workingCount} aria-live="polite">
                    {readCount} of {total} {total === 1 ? 'client' : 'clients'} read
                </div>
                <div className={classes.track}>
                    <div
                        className={classes.trackFill}
                        style={{ width: `${total === 0 ? 0 : Math.round((readCount / total) * 100)}%` }}
                    />
                </div>
            </div>
        </div>
    );
};

/**
 * The assembled answer: who matched, and who could not be read.
 *
 * Nothing is shown per client until every client has been read. A list that fills in one row at a
 * time invites acting on it before it is the whole answer.
 *
 * Clients the answer ruled out are deliberately not listed. They are the majority of any caseload and
 * none of them is the reason the question was asked; a list of people who do not match is a list
 * nobody reads.
 */
const SearchResults: FC = () => {
    const classes = useStyles();
    const searchState = useInstance(CaseloadSearchState);
    useSubscription(() => searchState);

    const { rows, caseloadSize } = searchState.state;
    const matched = searchState.getByVerdict('yes');
    const unavailable = searchState.getUnavailableRows();

    if (rows.length === 0 || searchState.getPendingCount() > 0) {
        return null;
    }

    const headline = matched.length === 1
        ? '1 client matched.'
        : matched.length > 1
            ? `${matched.length} clients matched.`
            : 'No clients matched.';

    return (
        <>
            <div className={classes.answer}>
                {matched.length === 0 && (
                    <div className={classes.headline} data-type="caseload-search-headline">{headline}</div>
                )}

                {matched.map(row => <CaseloadClientRow key={row.client.careMapKey.careMapId} row={row} />)}

                {unavailable.length > 0 && (
                    <>
                        <div className={classes.groupLabel}>Could Not Be Read</div>
                        {unavailable.map(row => <CaseloadClientRow key={row.client.careMapKey.careMapId} row={row} />)}
                    </>
                )}
            </div>

            {/* Only said when it changes what the results mean. A caseload that was fully read needs
                no announcement; one that was cut short does. */}
            {(searchState.isCaseloadCapped() || unavailable.length > 0) && (
                <div className={classes.coverage} data-type="caseload-search-coverage">
                    {searchState.isCaseloadCapped() && (
                        <>Covered {rows.length} of your {caseloadSize} clients. Ask again for the rest.</>
                    )}
                    {unavailable.length > 0 && (
                        <> {unavailable.length} could not be read, so they are not covered.</>
                    )}
                </div>
            )}
        </>
    );
};

export const CaseloadSearchSection: FC = () => {
    const classes = useStyles();

    const searchState = useInstance(CaseloadSearchState);
    useSubscription(() => searchState);

    const { query, rows, isRunning } = searchState.state;
    const caseload = searchState.getCaseload();

    if (caseload.length === 0) {
        return null;
    }

    const readCount = searchState.getReadCount();
    const searchedCount = rows.length;
    // measured against the clients the question will actually be put to, not the whole book, since a
    // question naming one person is never capped no matter how large the caseload is
    const willCap = searchState.getTargetedCaseload(query.trim()).length > MAX_CLIENTS_PER_SEARCH;

    const trimmedQuery = query.trim();

    // Named on the button rather than left to be discovered, because "reads every client's record"
    // and "reads Margaret Cole's record" are different enough undertakings to be worth confirming
    // before spending one.
    const clientsToSearch = searchState.getClientsToSearch(trimmedQuery);
    const isNarrowed = clientsToSearch.length < caseload.length;
    const readsLabel = clientsToSearch.length === 1
        ? `${clientsToSearch[0].name}'s notes, recordings and documents`
        : isNarrowed
            ? `${clientsToSearch.length} clients' notes, recordings and documents`
            : `all ${caseload.length} clients' notes, recordings and documents`
    ;

    return (
        <AskBeaCard
            heading="Ask across your clients"
            blurb="Find someone by name, or ask a question Bea answers by reading every client's record."
            placeholder="Search a name, or ask a question about all your clients"
            examples={EXAMPLE_QUESTIONS}
            query={query}
            onQueryChange={searchState.setQuery}
            isRunning={isRunning}
            onAsk={searchState.run}
            footer={<SearchResults />}
        >
            {trimmedQuery.length > 0 && rows.length === 0 && !isRunning && (
                <button
                    type="button"
                    className={classes.askRow}
                    data-id="caseload-search-ask"
                    onClick={() => searchState.run(query)}
                >
                    <img src="/assets/images/help/helper-bea.png" alt="" className={classes.beaAvatar} />
                    {/* Spans rather than divs: a button's content model is phrasing content, so the
                        JSS carries the block display instead of the tag. */}
                    <span className={classes.askText}>
                        <span className={`${classes.askLead} mp-mask`}>Ask Bea: "{trimmedQuery}"</span>
                        <span className={`${classes.askCaveat} mp-mask`}>
                            {willCap
                                ? `Reads the first ${MAX_CLIENTS_PER_SEARCH} of your ${caseload.length} clients' notes, recordings and documents. Takes a moment.`
                                : `Reads ${readsLabel}.${clientsToSearch.length > 1 ? ' Takes a moment.' : ''}`
                            }
                        </span>
                    </span>
                </button>
            )}

            {isRunning && <SearchProgress readsLabel={readsLabel} readCount={readCount} total={searchedCount} />}

            {(isRunning || rows.length > 0) && (
                <div className={classes.actions}>
                    {isRunning && (
                        <IonButton size="small" fill="clear" color="medium" onClick={() => searchState.cancel()}>
                            Stop
                        </IonButton>
                    )}

                    {!isRunning && rows.length > 0 && (
                        <IonButton
                            size="small"
                            fill="clear"
                            color="medium"
                            data-id="caseload-search-clear"
                            onClick={() => searchState.clear()}
                        >
                            Clear
                        </IonButton>
                    )}
                </div>
            )}

        </AskBeaCard>
    );
};
